import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArchiveBoxIcon, ArrowPathIcon, CheckCircleIcon, ClipboardDocumentIcon, CloudArrowDownIcon, CloudIcon,
  CommandLineIcon, EnvelopeIcon, ExclamationTriangleIcon, GlobeAltIcon, PauseIcon, PencilSquareIcon, PlayIcon,
  PlusIcon, ServerIcon, TrashIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  quelleAendern, quelleAnlegen, quelleEntfernen, quellePruefen, quellenArten, quellenListe, quelleVerbinden,
  type Quelle, type QuellenArt, type QuellenArten, type QuellenFeld, type QuellenPruefung,
} from '../../api/client';
import { useDialog } from '../../contexts/ConfirmContext';
import { vorZeit, zahl } from '../../utils/laufwerk';

/**
 * Karte „Quellen" (E32): Cloud-Speicher, Server und Postfächer, die powerCOMPANY selbst abholt — nur lesend. Der Admin
 * legt sie hier an (Art wählen, Zugangsdaten, „Verbindung testen"); Google/Microsoft/Dropbox verlangen danach eine
 * Anmeldung beim Anbieter. Jede Quelle erscheint im Laufwerk als eigener Ordner. Zugangsdaten kommen nie zurück —
 * die Karte zeigt nur, welche gesetzt sind.
 */

const INTERVALL_MS = 10_000;

export const ART_ICON: Record<string, typeof CloudIcon> = {
  google_drive: CloudIcon, onedrive: CloudIcon, dropbox: ArchiveBoxIcon, webdav: GlobeAltIcon,
  sftp: CommandLineIcon, ftp: ServerIcon, imap: EnvelopeIcon,
};

function Zustand({ q }: { q: Quelle }) {
  const wartend = q.zustand?.wartend ?? 0;
  if (!q.aktiv) return <span className="text-ps-dark-500">Pausiert — wird nicht gelesen</span>;
  if (q.oauth && !q.verbunden) return <span className="font-medium text-amber-800">Noch nicht verbunden</span>;
  if (q.meldung) return <span className="font-medium text-rose-700">{q.meldung}</span>;
  if (!q.zuletzt) return <span className="text-ps-dark-500">Wird zum ersten Mal gelesen …</span>;
  return (
    <span className="text-ps-dark-500">
      Zuletzt gelesen {vorZeit(q.zuletzt)}{wartend > 0 ? ` · ${zahl(wartend)} Dateien warten auf die Übertragung` : ''}
    </span>
  );
}

function punkt(q: Quelle) {
  if (!q.aktiv) return 'bg-ps-dark-300';
  if ((q.oauth && !q.verbunden) || !q.zuletzt) return 'bg-amber-400';
  return q.meldung ? 'bg-rose-500' : 'bg-emerald-500';
}

export default function QuellenKarte({ flash }: { flash: (msg: string, variant?: 'ok' | 'err') => void }) {
  const { confirm } = useDialog();
  const [quellen, setQuellen] = useState<Quelle[] | null>(null);
  const [arten, setArten] = useState<QuellenArten | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ quelle: Quelle | null } | null>(null);

  const laden = useCallback(async () => {
    try { setQuellen(await quellenListe()); setFehler(null); }
    catch (e: any) { setFehler(e?.message ?? String(e)); }
  }, []);

  useEffect(() => {
    void laden();
    quellenArten().then(setArten).catch(() => {});
    const t = setInterval(() => { if (!document.hidden) void laden(); }, INTERVALL_MS);
    return () => clearInterval(t);
  }, [laden]);

  const verbinden = async (q: Quelle) => {
    try { window.location.href = (await quelleVerbinden(q.id)).url; }
    catch (e: any) { flash(e?.message ?? String(e), 'err'); }
  };

  const pausieren = async (q: Quelle) => {
    try { await quelleAendern(q.id, { aktiv: !q.aktiv }); flash(q.aktiv ? `«${q.name}» pausiert.` : `«${q.name}» wird wieder gelesen.`); void laden(); }
    catch (e: any) { flash(e?.message ?? String(e), 'err'); }
  };

  const entfernen = async (q: Quelle) => {
    if (!await confirm({
      title: 'Quelle entfernen?',
      message: `«${q.name}» wird nicht mehr gelesen, und ihre ${zahl(q.dateien)} Dateien verschwinden aus powerCOMPANY (Suche, Fragen, Dateien). Bei ${q.art_titel} selbst wird nichts gelöscht.`,
      okLabel: 'Entfernen', danger: true,
    })) return;
    try { await quelleEntfernen(q.id); flash(`«${q.name}» entfernt.`); void laden(); }
    catch (e: any) { flash(e?.message ?? String(e), 'err'); }
  };

  return (
    <div className="card space-y-4" id="quellen">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="page-h1 flex items-center gap-2"><CloudArrowDownIcon className="h-5 w-5 text-ps-dark-400" /> Quellen</h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ps-dark-400">
            Cloud-Speicher, Server und E-Mail-Postfächer, die powerCOMPANY selbst abholt — nur lesend, es wird dort nie etwas
            verändert. Jede Quelle erscheint im Laufwerk als eigener Ordner und ist nach wenigen Minuten durchsuchbar.
          </p>
        </div>
        <button onClick={() => setDialog({ quelle: null })} disabled={!arten} className="btn-primary flex shrink-0 items-center gap-1.5 text-sm">
          <PlusIcon className="h-4 w-4" /> Quelle hinzufügen
        </button>
      </div>

      {fehler && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{fehler}</div>}

      {!quellen ? (
        <div className="space-y-2"><div className="shimmer h-3 w-1/3 rounded" /><div className="shimmer h-3 w-2/3 rounded" /></div>
      ) : quellen.length === 0 ? (
        <div className="rounded-lg border border-dashed border-ps-dark-200 px-4 py-6 text-center text-sm text-ps-dark-500">
          Noch keine Quelle. Fügen Sie Google Drive, OneDrive, Dropbox, einen WebDAV-, SFTP- oder FTP-Server oder ein
          E-Mail-Postfach hinzu.
        </div>
      ) : (
        <ul className="space-y-3">
          {quellen.map((q) => {
            const Icon = ART_ICON[q.art] ?? CloudIcon;
            return (
              <li key={q.id} className="rounded-lg border border-ps-dark-200 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-ps-dark-400" />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="break-words font-medium text-ps-dark-900">{q.name}</span>
                        <span className="badge badge-neutral">{q.art_titel}</span>
                      </div>
                      <div className="mt-0.5 flex items-start gap-1.5 text-xs">
                        <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${punkt(q)}`} />
                        <Zustand q={q} />
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                    {q.oauth && !q.verbunden && q.aktiv && (
                      <button onClick={() => void verbinden(q)} className="btn-primary px-3 py-1.5 text-xs">Jetzt verbinden</button>
                    )}
                    {q.oauth && q.verbunden && q.meldung && (
                      <button onClick={() => void verbinden(q)} className="btn-ghost text-xs">Neu verbinden</button>
                    )}
                    <button onClick={() => setDialog({ quelle: q })} className="btn-ghost flex items-center gap-1 text-xs" aria-label={`${q.name} bearbeiten`}>
                      <PencilSquareIcon className="h-4 w-4" /><span className="hidden sm:inline">Bearbeiten</span>
                    </button>
                    <button onClick={() => void pausieren(q)} className="btn-ghost flex items-center gap-1 text-xs" aria-label={q.aktiv ? 'Pausieren' : 'Fortsetzen'}>
                      {q.aktiv ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                      <span className="hidden sm:inline">{q.aktiv ? 'Pausieren' : 'Fortsetzen'}</span>
                    </button>
                    <button onClick={() => void entfernen(q)} className="rounded-lg p-1.5 text-rose-600 hover:bg-rose-50" aria-label={`${q.name} entfernen`}>
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <div className="min-w-0"><dt className="text-[11px] uppercase tracking-wide text-ps-dark-400">Dateien</dt><dd className="numerical font-medium text-ps-dark-800">{zahl(q.dateien)}</dd></div>
                  <div className="min-w-0"><dt className="text-[11px] uppercase tracking-wide text-ps-dark-400">Durchsuchbar</dt><dd className="numerical font-medium text-ps-dark-800">{zahl(q.durchsuchbar)}</dd></div>
                  <div className="min-w-0"><dt className="text-[11px] uppercase tracking-wide text-ps-dark-400">Ordner</dt><dd className="truncate font-medium text-ps-dark-800" title={q.name}>{q.name}/</dd></div>
                </dl>
              </li>
            );
          })}
        </ul>
      )}

      {dialog && arten && (
        <QuelleDialog arten={arten} quelle={dialog.quelle} onClose={() => setDialog(null)}
          onGespeichert={async (q, neu) => {
            setDialog(null);
            void laden();
            if (q.oauth && !q.verbunden && q.aktiv) {
              flash(`«${q.name}» gespeichert — jetzt bei ${q.art_titel} anmelden …`);
              await verbinden(q);
            } else {
              flash(neu ? `«${q.name}» hinzugefügt — die Dateien werden jetzt gelesen.` : `«${q.name}» gespeichert.`);
            }
          }} />
      )}
    </div>
  );
}

function Feld({ f, wert, setWert, gesetzt, pflicht }: {
  f: QuellenFeld; wert: any; setWert: (v: any) => void; gesetzt: boolean; pflicht?: boolean;
}) {
  const id = `quelle-${f.name}`;
  if (f.typ === 'ja_nein') {
    return (
      <label className="flex items-center gap-2 text-sm text-ps-dark-800">
        <input type="checkbox" checked={!!wert} onChange={(e) => setWert(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500" />
        {f.titel}
      </label>
    );
  }
  const noetig = pflicht ?? !f.optional;
  const platzhalter = f.geheim && gesetzt ? 'gespeichert — leer lassen, um es zu behalten' : (noetig ? '' : 'optional');
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-ps-dark-600">{f.titel}{noetig && <span className="text-rose-600"> *</span>}</label>
      {f.typ === 'auswahl' ? (
        <select id={id} value={wert ?? ''} onChange={(e) => setWert(e.target.value)} className="input-field">
          {(f.optionen ?? []).map(([w, t]) => <option key={w} value={w}>{t}</option>)}
        </select>
      ) : f.typ === 'mehrzeilig' ? (
        <textarea id={id} value={wert ?? ''} onChange={(e) => setWert(e.target.value)} rows={4} placeholder={platzhalter}
          spellCheck={false} autoComplete="off" className="input-field font-mono text-xs" />
      ) : (
        <input id={id} value={wert ?? ''} onChange={(e) => setWert(f.typ === 'zahl' ? e.target.value.replace(/\D/g, '') : e.target.value)}
          required={noetig && !(f.geheim && gesetzt)}
          type={f.typ === 'passwort' ? 'password' : f.typ === 'url' ? 'url' : 'text'} inputMode={f.typ === 'zahl' ? 'numeric' : undefined}
          placeholder={platzhalter} autoComplete={f.typ === 'passwort' ? 'new-password' : 'off'} spellCheck={false} className="input-field" />
      )}
      {f.hinweis && <p className="mt-1 text-[11px] text-ps-dark-400">{f.hinweis}</p>}
    </div>
  );
}

function QuelleDialog({ arten, quelle, onClose, onGespeichert }: {
  arten: QuellenArten; quelle: Quelle | null; onClose: () => void; onGespeichert: (q: Quelle, neu: boolean) => void;
}) {
  const [artId, setArtId] = useState<string | null>(quelle?.art ?? null);
  const art: QuellenArt | undefined = useMemo(() => arten.arten.find((a) => a.id === artId), [arten, artId]);
  const [name, setName] = useState(quelle?.name ?? '');
  const [werte, setWerte] = useState<Record<string, any>>({});
  const [erweitert, setErweitert] = useState(false);
  const [pruefung, setPruefung] = useState<QuellenPruefung | null>(null);
  const [busy, setBusy] = useState<'pruefen' | 'speichern' | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  // Startwerte: bestehende Konfiguration bzw. Standardwerte der Art; Geheimnisse bleiben leer
  useEffect(() => {
    if (!art) return;
    const w: Record<string, any> = {};
    for (const f of art.felder) {
      if (f.geheim) w[f.name] = '';
      else w[f.name] = quelle?.konfig?.[f.name] ?? f.standard ?? (f.typ === 'ja_nein' ? false : '');
    }
    setWerte(w);
    if (!quelle) setName(art.titel);
    setPruefung(null); setFehler(null);
    // Eigene App (Client-ID/-Secret) nur aufklappen, wenn die Quelle schon eine hat — sonst gilt die des Betreibers
    setErweitert(!!quelle?.konfig?.client_id);
  }, [art, quelle]);

  const ohneApp = !!art?.oauth && !art.betreiber_app;
  const anleitung = art?.oauth ? arten.anleitungen[art.oauth] : null;

  const aufteilen = () => {
    const konfig: Record<string, any> = {}; const geheim: Record<string, any> = {};
    for (const f of art?.felder ?? []) {
      const v = werte[f.name];
      if (f.geheim) { if (v) geheim[f.name] = v; }
      else konfig[f.name] = f.typ === 'zahl' ? (v === '' || v === undefined ? null : Number(v)) : v;
    }
    return { konfig, geheim };
  };

  const pruefen = async () => {
    if (!art) return;
    setBusy('pruefen'); setPruefung(null); setFehler(null);
    try { setPruefung(await quellePruefen({ art: art.id, ...aufteilen(), id: quelle?.id })); }
    catch (e: any) { setFehler(e?.message ?? String(e)); }
    finally { setBusy(null); }
  };

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!art) return;
    setBusy('speichern'); setFehler(null);
    try {
      const d = aufteilen();
      const q = quelle ? await quelleAendern(quelle.id, { name, ...d }) : await quelleAnlegen({ art: art.id, name, ...d });
      onGespeichert(q, !quelle);
    } catch (ex: any) { setFehler(ex?.message ?? String(ex)); }
    finally { setBusy(null); }
  };

  const kannPruefen = !!art && (!art.oauth || !!quelle?.verbunden);
  // Ohne App des Betreibers und ohne eigene App führt „anmelden" ins Leere
  const gesperrt = ohneApp && !erweitert && !quelle?.verbunden;
  const sichtbar = (art?.felder ?? []).filter((f) => !f.erweitert || erweitert);
  const hatErweitert = (art?.felder ?? []).some((f) => f.erweitert);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="quelle-titel" className="my-6 w-full max-w-2xl rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-ps-dark-100 px-4 py-3">
          <h3 id="quelle-titel" className="text-base font-semibold text-ps-dark-900">
            {quelle ? `«${quelle.name}» bearbeiten` : art ? `${art.titel} hinzufügen` : 'Quelle hinzufügen'}
          </h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-ps-dark-400 hover:bg-gray-100" aria-label="Schliessen"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        {!art ? (
          <div className="grid gap-2 p-4 sm:grid-cols-2">
            {arten.arten.map((a) => {
              const Icon = ART_ICON[a.id] ?? CloudIcon;
              return (
                <button key={a.id} onClick={() => setArtId(a.id)}
                  className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-left hover:border-primary-300 hover:bg-primary-50">
                  <Icon className="mt-0.5 h-6 w-6 shrink-0 text-primary-500" />
                  <span className="min-w-0">
                    <span className="block font-medium text-ps-dark-900">{a.titel}</span>
                    <span className="mt-0.5 block text-xs leading-snug text-ps-dark-500">{a.beschreibung}</span>
                    {a.oauth && !a.betreiber_app && (
                      <span className="mt-1 block text-[11px] font-medium text-amber-700">Noch nicht freigeschaltet (Betreiber-Konsole)</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <form onSubmit={speichern} className="space-y-4 p-4">
            {!quelle && (
              <button type="button" onClick={() => setArtId(null)} className="text-xs font-medium text-primary-700 hover:underline">← andere Art wählen</button>
            )}
            <p className="text-xs leading-relaxed text-ps-dark-500">{art.beschreibung}</p>
            <div>
              <label htmlFor="quelle-name" className="mb-1 block text-xs font-medium text-ps-dark-600">Name im Laufwerk <span className="text-rose-600">*</span></label>
              <input id="quelle-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required className="input-field" />
              <p className="mt-1 text-[11px] text-ps-dark-400">Unter diesem Ordner erscheinen die Dateien — auch für Rechtegruppen.</p>
            </div>

            {art.oauth && (
              <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 text-xs leading-relaxed text-primary-900">
                {quelle?.verbunden
                  ? <>Verbunden. Lesezugriff lässt sich jederzeit bei {anleitung?.titel} widerrufen.</>
                  : <>Nach dem Speichern melden Sie sich bei {anleitung?.titel} an und erlauben powerCOMPANY, Ihre Dateien zu <b>lesen</b>. Schreiben kann powerCOMPANY dort nicht.</>}
                {ohneApp && anleitung && !erweitert && (
                  <div className="mt-2 border-t border-primary-200 pt-2 text-ps-dark-700">
                    <b>Noch nicht freigeschaltet:</b> Der Betreiber hinterlegt die powerCOMPANY-App für {anleitung.titel} einmal
                    in der <a href={arten.konsole} target="_blank" rel="noreferrer" className="font-medium text-primary-700 underline">Betreiber-Konsole</a> unter
                    „Cloud-Anbindungen“ — danach genügt hier die Anmeldung.
                    <button type="button" onClick={() => setErweitert(true)} className="mt-1.5 block font-medium text-primary-700 hover:underline">
                      Stattdessen eine eigene App verwenden
                    </button>
                  </div>
                )}
                {ohneApp && anleitung && erweitert && (
                  <div className="mt-2 border-t border-primary-200 pt-2 text-ps-dark-700">
                    <b>Eigene App:</b> {anleitung.text}
                    <div className="mt-1.5 flex items-center gap-2">
                      <code className="min-w-0 flex-1 break-all rounded bg-white px-2 py-1 text-[11px]">{arten.rueckruf}</code>
                      <button type="button" className="btn-ghost shrink-0 px-2 py-1 text-xs" aria-label="Adresse kopieren"
                        onClick={() => void navigator.clipboard?.writeText(arten.rueckruf)}><ClipboardDocumentIcon className="h-4 w-4" /></button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              {sichtbar.map((f) => (
                <div key={f.name} className={f.typ === 'mehrzeilig' || f.typ === 'url' || f.hinweis ? 'sm:col-span-2' : ''}>
                  <Feld f={f} wert={werte[f.name]} setWert={(v) => setWerte((w) => ({ ...w, [f.name]: v }))}
                    gesetzt={!!quelle?.gesetzt.includes(f.name)}
                    pflicht={ohneApp && erweitert && (f.name === 'client_id' || f.name === 'client_secret') ? true : undefined} />
                </div>
              ))}
            </div>
            {hatErweitert && (!ohneApp || erweitert) && (
              <button type="button" onClick={() => setErweitert((x) => !x)} className="text-xs font-medium text-primary-700 hover:underline">
                {erweitert ? (ohneApp ? 'Doch keine eigene App' : 'Weniger Einstellungen') : 'Erweiterte Einstellungen'}
              </button>
            )}

            {pruefung && (pruefung.ok ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                <div className="flex items-center gap-1.5 font-medium"><CheckCircleIcon className="h-4 w-4" />
                  Verbindung in Ordnung — {pruefung.mehr ? 'mehr als ' : ''}{zahl(pruefung.anzahl)} {pruefung.anzahl === 1 ? 'Datei' : 'Dateien'} gefunden.
                </div>
                {!!pruefung.beispiele?.length && (
                  <ul className="mt-1.5 space-y-0.5 text-ps-dark-700">{pruefung.beispiele.slice(0, 5).map((b) => <li key={b} className="truncate">· {b}</li>)}</ul>
                )}
                {pruefung.hostschluessel && (
                  <p className="mt-1.5 break-all text-ps-dark-600">Hostschlüssel des Servers: <code>{pruefung.hostschluessel}</code> — wird beim ersten Lesen gemerkt und danach jedes Mal geprüft.</p>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-1.5 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0" /> {pruefung.meldung}
              </div>
            ))}
            {fehler && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">{fehler}</div>}

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-ps-dark-100 pt-3">
              {kannPruefen && (
                <button type="button" onClick={() => void pruefen()} disabled={!!busy} className="btn-ghost flex items-center gap-1.5 text-sm">
                  <ArrowPathIcon className={`h-4 w-4 ${busy === 'pruefen' ? 'animate-spin' : ''}`} /> Verbindung testen
                </button>
              )}
              <button type="button" onClick={onClose} className="btn-ghost text-sm">Abbrechen</button>
              <button type="submit" disabled={!!busy || !name.trim() || gesperrt} className="btn-primary text-sm"
                title={gesperrt ? 'Erst freischalten (Betreiber-Konsole) oder eine eigene App eintragen' : undefined}>
                {busy === 'speichern' ? 'Speichert …' : art.oauth && !quelle?.verbunden ? 'Speichern und anmelden' : 'Speichern'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
